export const AUTOSAVE_VERSION = 1
export const DEFAULT_AUTOSAVE_TTL_MS = 7 * 24 * 60 * 60 * 1000
export const DEFAULT_AUTOSAVE_INTERVAL_MS = 2 * 60 * 1000

export interface AutosaveIdentity {
  workspaceId: string
  userId: string
  formId: string
  recordId?: string
}

interface AutosaveEnvelope<T> {
  version: number
  savedAt: number
  expiresAt: number
  data: T
}

export type AutosaveReadResult<T> =
  | { outcome: "empty" }
  | { outcome: "restored"; data: T; savedAt: number; expiresAt: number }
  | { outcome: "expired" }
  | { outcome: "corrupt" }
  | { outcome: "incompatible" }
  | { outcome: "error"; error: Error }

export type AutosaveWriteResult =
  | { outcome: "saved"; savedAt: number; expiresAt: number }
  | { outcome: "quota-exceeded"; error: Error }
  | { outcome: "error"; error: Error }

export interface AutosaveStorageOptions {
  storage?: Storage
  ttlMs?: number
  now?: () => number
}

// This is deliberately centralized so every form receives the same protection.
// Matching ignores case and punctuation (for example, "api_token" and "apiToken").
const SENSITIVE_FIELD_NAMES = new Set([
  "password",
  "passphrase",
  "secret",
  "token",
  "accesstoken",
  "refreshtoken",
  "authtoken",
  "authorization",
  "cookie",
  "session",
  "sessionid",
  "apikey",
  "privatekey",
  "clientsecret",
  "credential",
  "credentials",
  "creditcard",
  "cardnumber",
  "cvv",
  "cvc",
  "pin",
  "ssn",
  "phone",
  "phonenumber",
  "fullname",
  "address",
])

// Draft storage is not an appropriate place for arbitrary prose entered by a
// user. Keep this separate from the credential/PII list above: identifiers,
// quantities, selections, and other structured fields remain restorable.
const FREE_TEXT_FIELD_SUFFIXES = [
  "note",
  "notes",
  "reason",
  "reasons",
  "comment",
  "comments",
  "message",
  "messages",
  "description",
  "descriptions",
]

function normalizedFieldName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "")
}

export function isSensitiveAutosaveField(name: string): boolean {
  const normalized = normalizedFieldName(name)
  return (
    SENSITIVE_FIELD_NAMES.has(normalized) ||
    FREE_TEXT_FIELD_SUFFIXES.some(
      (suffix) => normalized === suffix || normalized.endsWith(suffix),
    ) ||
    normalized.endsWith("password") ||
    normalized.endsWith("secret") ||
    normalized.endsWith("token") ||
    normalized.endsWith("apikey")
  )
}

/**
 * Returns a JSON-safe copy with sensitive fields removed at every depth.
 * Dates retain JSON.stringify's normal ISO representation. Circular state is
 * rejected rather than silently writing an incomplete draft.
 */
export function sanitizeAutosaveState<T>(state: T): T {
  const seen = new WeakSet<object>()

  const visit = (value: unknown): unknown => {
    if (value === null || typeof value !== "object") return value
    if (seen.has(value)) throw new TypeError("Autosave state contains a circular reference")
    seen.add(value)

    if (Array.isArray(value)) {
      const result = value.map(visit)
      seen.delete(value)
      return result
    }

    const result: Record<string, unknown> = {}
    for (const [key, child] of Object.entries(value)) {
      if (!isSensitiveAutosaveField(key)) result[key] = visit(child)
    }
    seen.delete(value)
    return result
  }

  return visit(state) as T
}

function assertIdentity(identity: AutosaveIdentity): void {
  for (const [name, value] of Object.entries(identity)) {
    if (name === "recordId" && value === undefined) continue
    if (typeof value !== "string" || value.trim() === "") {
      throw new TypeError(`Autosave identity ${name} must be a non-empty string`)
    }
  }
}

// A stable 64-bit FNV-1a hash keeps user/workspace/record identifiers out of
// localStorage key names while preserving strict identity isolation.
function hashIdentity(value: string): string {
  let hash = 0xcbf29ce484222325n
  for (let index = 0; index < value.length; index += 1) {
    hash ^= BigInt(value.charCodeAt(index))
    hash = BigInt.asUintN(64, hash * 0x100000001b3n)
  }
  return hash.toString(36)
}

export function autosaveStorageKey(identity: AutosaveIdentity): string {
  assertIdentity(identity)
  const serialized = JSON.stringify([
    identity.workspaceId,
    identity.userId,
    identity.formId,
    identity.recordId ?? "",
  ])
  return `infrastock:autosave:v${AUTOSAVE_VERSION}:${hashIdentity(serialized)}`
}

function defaultStorage(): Storage {
  if (typeof window === "undefined" || !window.localStorage) {
    throw new Error("localStorage is not available")
  }
  return window.localStorage
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}

function isQuotaError(error: unknown): boolean {
  const namedError = error as { name?: string; code?: number }
  return (
    !!error &&
    (namedError.name === "QuotaExceededError" ||
      namedError.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
      namedError.code === 22 ||
      namedError.code === 1014)
  )
}

export class AutosaveStorage {
  private readonly storage: Storage | null
  private readonly initializationError: Error | null
  private readonly ttlMs: number
  private readonly now: () => number

  constructor(options: AutosaveStorageOptions = {}) {
    try {
      this.storage = options.storage ?? defaultStorage()
      this.initializationError = null
    } catch (error) {
      this.storage = null
      this.initializationError = toError(error)
    }
    this.ttlMs = options.ttlMs ?? DEFAULT_AUTOSAVE_TTL_MS
    this.now = options.now ?? Date.now
    if (!Number.isFinite(this.ttlMs) || this.ttlMs <= 0) {
      throw new TypeError("Autosave TTL must be greater than zero")
    }
  }

  read<T>(identity: AutosaveIdentity): AutosaveReadResult<T> {
    const key = autosaveStorageKey(identity)
    if (!this.storage) {
      return {
        outcome: "error",
        error: this.initializationError ?? new Error("localStorage is not available"),
      }
    }
    let raw: string | null
    try {
      raw = this.storage.getItem(key)
    } catch (error) {
      return { outcome: "error", error: toError(error) }
    }
    if (raw === null) return { outcome: "empty" }

    let envelope: unknown
    try {
      envelope = JSON.parse(raw)
    } catch {
      this.removeInvalid(key)
      return { outcome: "corrupt" }
    }

    if (
      !envelope ||
      typeof envelope !== "object" ||
      typeof (envelope as AutosaveEnvelope<T>).savedAt !== "number" ||
      typeof (envelope as AutosaveEnvelope<T>).expiresAt !== "number" ||
      !("data" in envelope)
    ) {
      this.removeInvalid(key)
      return { outcome: "corrupt" }
    }

    const parsed = envelope as AutosaveEnvelope<T>
    if (parsed.version !== AUTOSAVE_VERSION) {
      this.removeInvalid(key)
      return { outcome: "incompatible" }
    }
    if (parsed.expiresAt <= this.now()) {
      this.removeInvalid(key)
      return { outcome: "expired" }
    }
    return {
      outcome: "restored",
      data: parsed.data,
      savedAt: parsed.savedAt,
      expiresAt: parsed.expiresAt,
    }
  }

  write<T>(identity: AutosaveIdentity, state: T): AutosaveWriteResult {
    const savedAt = this.now()
    const expiresAt = savedAt + this.ttlMs
    if (!this.storage) {
      return {
        outcome: "error",
        error: this.initializationError ?? new Error("localStorage is not available"),
      }
    }
    try {
      const envelope: AutosaveEnvelope<T> = {
        version: AUTOSAVE_VERSION,
        savedAt,
        expiresAt,
        data: sanitizeAutosaveState(state),
      }
      this.storage.setItem(autosaveStorageKey(identity), JSON.stringify(envelope))
      return { outcome: "saved", savedAt, expiresAt }
    } catch (error) {
      const normalized = toError(error)
      return isQuotaError(error)
        ? { outcome: "quota-exceeded", error: normalized }
        : { outcome: "error", error: normalized }
    }
  }

  clear(identity: AutosaveIdentity): Error | null {
    if (!this.storage) {
      return this.initializationError ?? new Error("localStorage is not available")
    }
    try {
      this.storage.removeItem(autosaveStorageKey(identity))
      return null
    } catch (error) {
      return toError(error)
    }
  }

  private removeInvalid(key: string): void {
    if (!this.storage) return
    try {
      this.storage.removeItem(key)
    } catch {
      // The read outcome remains useful even when privacy-mode storage refuses
      // cleanup. A later read will make the same safe rejection.
    }
  }
}